import {
  Component,
  OnInit,
  AfterViewInit,
  inject,
  signal,
} from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { initFlowbite } from 'flowbite';
import {
  HeaderComponent,
  HeaderConfig,
} from './shared/components/header/header.component';
import {
  FooterComponent,
  FooterConfig,
} from './shared/components/footer/footer.component';
import {
  BottomNavigationComponent,
  BottomNavigationConfig,
} from './shared/components/bottom-navigation/bottom-navigation.component';
import { ConsentPopupComponent } from './shared/components/consent-popup/consent-popup.component';
import { ToastComponent } from './shared/components/toast/toast.component';
import { BusinessContactComponent } from './shared/components/business-contact/business-contact.component';
import { SplashScreenComponent } from './shared/components/splash-screen/splash-screen.component';
import { SwipeNavigationDirective } from './shared/directives/swipe-navigation.directive';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './core/services/auth.service';
import { AppsFlyerService } from './core/services/appsflyer.service';
import { NavigationOverlayService } from './core/services/navigation-overlay.service';
import { BackButtonService } from './core/services/back-button.service';
import { filter } from 'rxjs/operators';
import { environment } from '../environments/environment';
import {
  routeAnimations,
  getAnimationState,
} from './core/animations/route-animations';

// Declare the pre-splash function from index.html
declare global {
  interface Window {
    hidePreSplash?: () => void;
  }
}

@Component({
  template: `
    <!-- Splash Screen (Angular-controlled) -->
    <app-splash-screen />

    <!-- Main App Layout - Fixed height sections -->
    <div class="h-screen flex flex-col overscroll-none overflow-hidden">
      <!-- Header - ~10.7% of screen (102/954px) -->
      <app-header [config]="headerConfig" class="flex-shrink-0"></app-header>

      <!-- Main Content (with swipe navigation and route animations) - fills remaining space -->
      <main
        appSwipeNavigation
        [swipeEnabled]="false"
        [ignoreSelector]="
          'app-category-filter, app-advanced-filters, .swiper, .carousel, .leaflet-container, app-image-zoom-modal'
        "
        [allowedContainers]="
          '.offers-background, .menus-background, .favorites-container'
        "
        class="flex-1 relative min-h-0"
        [class.overflow-hidden]="!isAdminRoute()"
        [class.overflow-y-auto]="isAdminRoute()"
        [@routeAnimations]="animationState()"
      >
        <router-outlet />
        @if (!hideBusinessContact()) {
          <app-business-contact />
        }
      </main>

      <!-- Footer (hidden in mobile app context) -->
      <app-footer [config]="footerConfig" class="flex-shrink-0"></app-footer>

      <!-- Bottom Navigation Menu - ~8.1% of screen (77/954px) -->
      <app-bottom-navigation
        [config]="bottomNavConfig"
        class="flex-shrink-0"
      ></app-bottom-navigation>

      <!-- Consent Popup (shown after splash hides) -->
      <app-consent-popup />

      <!-- Toast Notifications -->
      <app-toast />
    </div>
  `,
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    BottomNavigationComponent,
    ConsentPopupComponent,
    ToastComponent,
    SplashScreenComponent,
    SwipeNavigationDirective,
    BusinessContactComponent,
  ],
  animations: [routeAnimations],
})
export class AppComponent implements OnInit, AfterViewInit {
  title = 'frontend-user-angular';
  headerConfig: HeaderConfig = {};
  footerConfig: FooterConfig = {};
  bottomNavConfig: BottomNavigationConfig = {};

  /** Current animation state for route transitions */
  animationState = signal<string>('');
  hideBusinessContact = signal(false);
  isAdminRoute = signal(false);

  private authService = inject(AuthService);
  private backButtonService = inject(BackButtonService);
  private appsFlyerService = inject(AppsFlyerService);
  private translateService = inject(TranslateService);

  /** Navigation overlay service for covering header during route transitions */
  navigationOverlay = inject(NavigationOverlayService);

  constructor(private router: Router) {
    console.log('🏗️ AppComponent constructor called');

    const savedLang = localStorage.getItem('gastronauta_lang');
    this.translateService.use(savedLang && ['es', 'en'].includes(savedLang) ? savedLang : 'es');

    // Listen to route changes to update header and footer configuration
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.updateLayoutConfig(event.url);
        // Update animation state for route transitions
        this.animationState.set(getAnimationState(event.url));
        this.isAdminRoute.set(event.url.startsWith('/admin'));
        this.hideBusinessContact.set(
          event.url.startsWith('/places') || event.url.startsWith('/admin') || event.url.startsWith('/submit-offer') || event.url.startsWith('/my-submissions'),
        );
        // Scroll to top on navigation (instant for animated transitions)
        window.scrollTo({ top: 0, behavior: 'instant' });

        // Track screen view in AppsFlyer
        this.appsFlyerService.trackScreenView(event.urlAfterRedirects);

        // Hide navigation overlay after route change completes
        // Use a small delay to ensure the animation has started
        setTimeout(() => {
          this.navigationOverlay.hide();
        }, 50);
      });
  }

  ngOnInit(): void {
    console.log('🚀 AppComponent ngOnInit called');
    initFlowbite();

    // Ensure light theme is applied on app initialization
    document.documentElement.classList.remove('dark');
    document.documentElement.setAttribute('data-theme', 'light');

    // Initialize native back button handler for Android/iOS
    this.backButtonService.initialize();
    // Initialize AppsFlyer SDK (only runs on native iOS/Android)
    this.appsFlyerService.initSDK();

    // Google Sign-In has been removed - app operates with anonymous sessions only
  }

  ngAfterViewInit(): void {
    // Hide the pre-Angular splash (index.html) now that Angular has loaded
    if (window.hidePreSplash) {
      window.hidePreSplash();
    }
  }

  private updateLayoutConfig(url: string) {
    // Get the current route configuration
    const route = this.router.routerState.root.firstChild;
    const routeData = route?.snapshot?.data || {};

    // Check if we're on an admin route
    const isAdminRoute = url.startsWith('/admin');

    // Update header configuration from route data
    this.headerConfig = {
      showLogo: routeData['header']?.showLogo ?? true,
      showNavigation: routeData['header']?.showNavigation ?? true,
      showUserActions: routeData['header']?.showUserActions ?? true,
      showMobileMenu: routeData['header']?.showMobileMenu ?? true,
      customTitle: routeData['header']?.customTitle,
      customSubtitle: routeData['header']?.customSubtitle,
      backgroundColor: routeData['header']?.backgroundColor ?? 'bg-white',
      textColor: routeData['header']?.textColor ?? 'text-gray-900',
    };

    // Update footer configuration from route data
    // Hide footer on admin routes
    this.footerConfig = {
      showFooter: isAdminRoute
        ? false
        : (routeData['footer']?.showFooter ?? true),
      showBrand: routeData['footer']?.showBrand ?? true,
      showQuickLinks: routeData['footer']?.showQuickLinks ?? true,
      showSupport: routeData['footer']?.showSupport ?? true,
      showSocial: routeData['footer']?.showSocial ?? true,
      showBottomSection: routeData['footer']?.showBottomSection ?? true,
      backgroundColor: routeData['footer']?.backgroundColor ?? 'bg-gray-900',
      textColor: routeData['footer']?.textColor ?? 'text-white',
    };

    // Update bottom navigation configuration from route data
    this.bottomNavConfig = {
      showNavigation: routeData['bottomNav']?.showNavigation ?? true,
      items: routeData['bottomNav']?.items ?? this.getDefaultNavItems(),
      backgroundColor: routeData['bottomNav']?.backgroundColor ?? 'bg-white',
      textColor: routeData['bottomNav']?.textColor ?? 'text-gray-700',
      activeColor: routeData['bottomNav']?.activeColor ?? 'text-primary-600',
      showLabels: routeData['bottomNav']?.showLabels ?? true,
      showIcons: routeData['bottomNav']?.showIcons ?? true,
      position: routeData['bottomNav']?.position ?? 'bottom',
      height: routeData['bottomNav']?.height ?? '4.5rem',
      shadow: routeData['bottomNav']?.shadow ?? true,
      border: routeData['bottomNav']?.border ?? true,
    };
  }

  private getDefaultNavItems() {
    return [
      {
        icon: 'fas fa-tags',
        label: 'Ofertas',
        route: '/offers',
        requiresAuth: false,
      },
      {
        icon: 'fas fa-utensils',
        label: 'Menús',
        route: '/menus',
        requiresAuth: false,
      },
      {
        icon: 'fas fa-map-marker-alt',
        label: 'Mapa',
        route: '/places',
        requiresAuth: false,
      },
      {
        icon: 'fas fa-heart',
        label: 'Favoritos',
        route: '/liked-offers',
        requiresAuth: true,
      },
    ];
  }
}
